"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IkeDrawState = exports.DEFAULT_SCALE = exports.IkeDrawTool = void 0;
var tslib_1 = require("tslib");
var mobx_1 = require("mobx");
var gamma_brightnessCorrection_1 = require("../model/collection/brightnessCorrection/gamma.brightnessCorrection");
var free_cropParameter_1 = require("../model/collection/cropParameter/free.cropParameter");
var hidden_imageVisibility_1 = require("../model/collection/imageVisibility/hidden.imageVisibility");
var unit_1 = require("../unit");
var ArrangeAnnotations_1 = require("./ArrangeAnnotations");
var image_zoom_binding_1 = require("./bindings/image.zoom.binding");
var IkeDrawTool;
(function (IkeDrawTool) {
    IkeDrawTool["MARKER"] = "marker";
    IkeDrawTool["LINE"] = "line";
    IkeDrawTool["LINK"] = "link";
    IkeDrawTool["BASE"] = "base";
})(IkeDrawTool || (exports.IkeDrawTool = IkeDrawTool = {}));
exports.DEFAULT_SCALE = 4;
var IkeDrawState = /** @class */ (function () {
    function IkeDrawState(draw) {
        this.zoom = IkeDrawState.ZOOM_DEFAULT;
        this.cablemeasure = IkeDrawState.CABLEMEASURE_DEFAULT;
        this.showCablemeasure = false;
        this._brightness = 1;
        this._imageVisibilityHidden = false;
        this._cropX = 0;
        this._cropY = 0;
        this._cropWidth = 0;
        this._cropHeight = 0;
        /**
         * this scale is actually static, the zoom is used to determine how large things are drawn on the screen.
         * often times the svgRender ctx/options will have a 'scale' property, which is calculated using zoom level and NOT this scale.
         */
        this.scale = exports.DEFAULT_SCALE;
        this.centerX = 0;
        this.centerY = 0;
        this.selectedRegion = null;
        this.selectedId = null;
        this.brightnessCorrectionId = null;
        this.imageVisibilityId = null;
        this.defaultBrightness = 1;
        this.defaultImageVisibilityHidden = false;
        this.defaultCrop = { x: 0, y: 0, width: 0, height: 0 };
        this.cropParameterId = null;
        this.hoverId = null;
        this.moveId = null;
        this.linkFieldIds = [];
        /* Trigger the properties dialog */
        this.propertiesId = null;
        this.isCrosshairVisible = true;
        this.isDateLabelVisible = false;
        this.isBearingLabelVisible = false;
        this.isRollVisible = false;
        this.isInfoVisible = false;
        this.visibleInsightDetections = [];
        this.mouseDownX = null;
        this.mouseDownY = null;
        this._tool = null;
        this.draw = draw;
        //if there is an already existing brightnessCorrection value, we apply it.
        var values = this.draw.brightnessCorrectionState.values();
        var id = '';
        //We go through all the brightness correction for this capture and keep the last id (there shall be only one brightness correction)
        for (var _i = 0, values_1 = values; _i < values_1.length; _i++) {
            var val = values_1[_i];
            id = val.id;
        }
        if (draw.brightnessCorrectionState.hasBrightnessCorrection(id)) {
            var gamma = draw.brightnessCorrectionState.getBrightnessCorrection(id);
            this.setBrightnessCorrectionId(id);
            this.setBrightness(gamma.exponent);
            this.defaultBrightness = gamma.exponent;
        }
        if (draw.imageVisibilityState.hasImageVisibility()) {
            var visibility = draw.imageVisibilityState.getImageVisibility();
            this.setImageVisibilityId(visibility.id);
            this.setImageVisibilityHidden(visibility.hidden);
            this.defaultImageVisibilityHidden = visibility.hidden;
        }
        var cropValues = this.draw.cropParameterState.values();
        var cropId = '';
        for (var _a = 0, cropValues_1 = cropValues; _a < cropValues_1.length; _a++) {
            var val = cropValues_1[_a];
            cropId = val.id;
        }
        if (draw.cropParameterState.hasCropParameter(cropId)) {
            var free = draw.cropParameterState.getCropParameter(cropId);
            this.setCropParameterId(cropId);
            this.setCrop(free.x, free.y, free.width, free.height);
            if (free.width > 0 && free.height > 0) {
                this.setZoom(draw.capture.resolution.width / free.width); //zoom lvl
                this.setCenter(free.x + (free.width * 0.5), free.y + (free.height * 0.5));
            }
            this.defaultCrop = free;
        }
        if (this.draw.drawState.hasInsightDetections()) {
            this.visibleInsightDetections = ['marker'];
        }
    }
    Object.defineProperty(IkeDrawState.prototype, "areInsightDetectionsVisible", {
        get: function () {
            return this.visibleInsightDetections.length > 0;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDrawState.prototype, "tool", {
        get: function () {
            // If the image is not classed as a markable image, then no tools are available.
            if (!this.draw.isMarkable) {
                return null;
            }
            // If the image contains any user created measurements, then use the selected tool
            // Note: A standard base measurement is considered a user measurement
            if (this.draw.drawState.hasUserMeasurements()) {
                return this._tool;
            }
            if (
            // If the image contains insight measurements
            this.draw.drawState.hasInsightDetections()
                // and those measurements are visible
                && this.visibleInsightDetections.includes('marker')
                // and there is an insight base measurement
                && this.draw.drawState.insightBase) {
                // Then return the selected tool
                return this._tool;
            }
            // Otherwise, only the base tool is available
            return 'base';
        },
        enumerable: false,
        configurable: true
    });
    IkeDrawState.prototype.setTool = function (tool) {
        this._tool = tool;
        this.linkFieldIds = [];
    };
    IkeDrawState.prototype.toggleTool = function (tool) {
        if (this.tool === tool) {
            this._tool = null;
        }
        else {
            this._tool = tool;
        }
    };
    Object.defineProperty(IkeDrawState.prototype, "brightness", {
        get: function () {
            var brightness = this._brightness;
            if (brightness == null || isNaN(brightness)) {
                return IkeDrawState.BRIGHTNESS_DEFAULT;
            }
            return brightness;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDrawState.prototype, "imageVisibilityHidden", {
        get: function () {
            var imageHidden = this._imageVisibilityHidden;
            if (!imageHidden) {
                return IkeDrawState.IMAGE_VISIBILITY_HIDDEN_DEFAULT;
            }
            return imageHidden;
        },
        enumerable: false,
        configurable: true
    });
    IkeDrawState.prototype.setBrightness = function (brightness) {
        if (brightness == null) {
            this._brightness = null;
            return;
        }
        //if there is no existing brightness correction state yet, we create one before setting its value.
        if (this.brightnessCorrectionId == null) {
            var newGammaId = this.draw.brightnessCorrectionState.Mutator.create('gamma');
            this.setBrightnessCorrectionId(newGammaId);
        }
        if (brightness > IkeDrawState.BRIGHTNESS_DEFAULT - IkeDrawState.BRIGHTNESS_APPROXIMATION_WINDOW &&
            brightness < IkeDrawState.BRIGHTNESS_DEFAULT + IkeDrawState.BRIGHTNESS_APPROXIMATION_WINDOW) {
            this._brightness = unit_1.IkeUnit.round(brightness, 2);
        }
        else {
            this._brightness = unit_1.IkeUnit.round(brightness, 5);
        }
        if (this.brightnessCorrectionId) {
            var gamma = this.draw.brightnessCorrectionState.getBrightnessCorrection(this.brightnessCorrectionId);
            if (gamma_brightnessCorrection_1.CollectionModelGammaBrightnessCorrection.isGamma(gamma)) {
                gamma.Mutator.setExponent(this._brightness);
            }
        }
    };
    IkeDrawState.prototype.setImageVisibilityHidden = function (hidden) {
        //if there is no existing image visibility state yet, we create one before setting its value.
        if (this.imageVisibilityId == null) {
            var newHiddenImageVisibilityId = this.draw.imageVisibilityState.Mutator.create('hidden');
            this.setImageVisibilityId(newHiddenImageVisibilityId);
        }
        var visibility = this.draw.imageVisibilityState.getImageVisibility();
        this._imageVisibilityHidden = hidden;
        if (hidden_imageVisibility_1.CollectionModelHiddenImageVisibility.isHidden(visibility)) {
            visibility.Mutator.setHidden(hidden);
        }
    };
    IkeDrawState.prototype.toggleImageVisibility = function () {
        this.setImageVisibilityHidden(!this._imageVisibilityHidden);
    };
    IkeDrawState.prototype.brightnessIncrease = function () {
        if (this.canIncreaseBrightness) {
            var newBrightness = this.brightness / IkeDrawState.BRIGHTNESS_DELTA;
            this.setBrightness(newBrightness);
        }
    };
    IkeDrawState.prototype.brightnessDecrease = function () {
        if (this.canDecreaseBrightness) {
            var newBrightness = this.brightness * IkeDrawState.BRIGHTNESS_DELTA;
            this.setBrightness(newBrightness);
        }
    };
    Object.defineProperty(IkeDrawState.prototype, "canIncreaseBrightness", {
        get: function () {
            var result = this.brightness > IkeDrawState.BRIGHTNESS_MIN;
            return result;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDrawState.prototype, "canDecreaseBrightness", {
        get: function () {
            var result = this.brightness < IkeDrawState.BRIGHTNESS_MAX;
            return result;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDrawState.prototype, "canIncreaseCablemeasure", {
        get: function () {
            return ((this.cablemeasure + IkeDrawState.CABLEMEASURE_STEP) <= IkeDrawState.CABLEMEASURE_MAX);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDrawState.prototype, "canDecreaseCablemeasure", {
        get: function () {
            return ((this.cablemeasure - IkeDrawState.CABLEMEASURE_STEP) >= IkeDrawState.CABLEMEASURE_MIN);
        },
        enumerable: false,
        configurable: true
    });
    IkeDrawState.prototype.brightnessReset = function () {
        this.setBrightness(IkeDrawState.BRIGHTNESS_DEFAULT);
    };
    Object.defineProperty(IkeDrawState.prototype, "markerOffsetWidth", {
        get: function () {
            return 200 - (this.zoom * 10);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDrawState.prototype, "cropX", {
        get: function () {
            var cropX = this._cropX;
            if (cropX == null || isNaN(cropX)) {
                return IkeDrawState.CROP_DEFAULT;
            }
            return cropX;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDrawState.prototype, "cropY", {
        get: function () {
            var cropY = this._cropY;
            if (cropY == null || isNaN(cropY)) {
                return IkeDrawState.CROP_DEFAULT;
            }
            return cropY;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDrawState.prototype, "cropWidth", {
        get: function () {
            var cropWidth = this._cropWidth;
            if (cropWidth == null || isNaN(cropWidth)) {
                return IkeDrawState.CROP_DEFAULT;
            }
            return cropWidth;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(IkeDrawState.prototype, "cropHeight", {
        get: function () {
            var cropHeight = this._cropHeight;
            if (cropHeight == null || isNaN(cropHeight)) {
                return IkeDrawState.CROP_DEFAULT;
            }
            return cropHeight;
        },
        enumerable: false,
        configurable: true
    });
    IkeDrawState.prototype.setCrop = function (x, y, width, height) {
        if (x == null || y == null || width == null || height == null) {
            this._cropX = null;
            this._cropY = null;
            this._cropWidth = null;
            this._cropHeight = null;
            return;
        }
        //if there is no existing crop parameter state yet, we create one before setting its value.
        if (this.cropParameterId == null) {
            var newFreeId = this.draw.cropParameterState.Mutator.create('free');
            this.setCropParameterId(newFreeId);
        }
        this._cropX = x;
        this._cropY = y;
        this._cropWidth = width;
        this._cropHeight = height;
        if (this.cropParameterId) {
            var free = this.draw.cropParameterState.getCropParameter(this.cropParameterId);
            if (free_cropParameter_1.CollectionModelFreeCropParameter.isFree(free)) {
                free.Mutator.setCrop(this._cropX, this._cropY, this._cropWidth, this._cropHeight);
            }
        }
    };
    IkeDrawState.prototype.cropReset = function () {
        this.setCrop(IkeDrawState.CROP_DEFAULT, IkeDrawState.CROP_DEFAULT, IkeDrawState.CROP_DEFAULT, IkeDrawState.CROP_DEFAULT);
    };
    ///////////////////////////////////
    IkeDrawState.prototype.setCrosshair = function (show) {
        this.isCrosshairVisible = show;
    };
    IkeDrawState.prototype.setDatelabel = function (show) {
        this.isDateLabelVisible = show;
    };
    IkeDrawState.prototype.setBearingLabelVisible = function (show) {
        this.isBearingLabelVisible = show;
    };
    Object.defineProperty(IkeDrawState.prototype, "center", {
        get: function () {
            return {
                x: this.centerX,
                y: this.centerY
            };
        },
        enumerable: false,
        configurable: true
    });
    IkeDrawState.prototype.setHovered = function (hoverId) {
        this.hoverId = hoverId;
    };
    IkeDrawState.prototype.setCenter = function (x, y) {
        this.centerX = x;
        this.centerY = y;
    };
    IkeDrawState.prototype.setSelected = function (id, region) {
        this.selectedId = id;
        this.selectedRegion = region;
    };
    IkeDrawState.prototype.setBrightnessCorrectionId = function (id) {
        this.brightnessCorrectionId = id;
    };
    IkeDrawState.prototype.setImageVisibilityId = function (id) {
        this.imageVisibilityId = id;
    };
    IkeDrawState.prototype.setCropParameterId = function (id) {
        this.cropParameterId = id;
    };
    IkeDrawState.prototype.setZoom = function (zoom, x, y) {
        if (x != null && y != null) {
            this.setCenter(x, y);
        }
        this.zoom = unit_1.IkeUnit.round(zoom, 2);
    };
    IkeDrawState.prototype.zoomIncrease = function () {
        this.setZoom(Math.min(IkeDrawState.ZOOM_MAX, this.zoom * IkeDrawState.ZOOM_DELTA));
    };
    IkeDrawState.prototype.zoomDecrease = function () {
        this.setZoom(Math.max(IkeDrawState.ZOOM_DEFAULT, this.zoom * (1 / IkeDrawState.ZOOM_DELTA)));
    };
    IkeDrawState.prototype.zoomReset = function () {
        this.setZoom(IkeDrawState.ZOOM_DEFAULT);
    };
    IkeDrawState.prototype.toggleCablemeasure = function () {
        this.showCablemeasure = !this.showCablemeasure;
    };
    IkeDrawState.prototype.cablemeasureIncrease = function () {
        var actualMeasure = this.cablemeasure + IkeDrawState.CABLEMEASURE_STEP;
        if (actualMeasure > IkeDrawState.CABLEMEASURE_MAX) {
            actualMeasure = IkeDrawState.CABLEMEASURE_MAX;
        }
        this.cablemeasure = actualMeasure;
    };
    IkeDrawState.prototype.cablemeasureDecrease = function () {
        var actualMeasure = this.cablemeasure - IkeDrawState.CABLEMEASURE_STEP;
        if (actualMeasure < IkeDrawState.CABLEMEASURE_MIN) {
            actualMeasure = IkeDrawState.CABLEMEASURE_MIN;
        }
        this.cablemeasure = actualMeasure;
    };
    IkeDrawState.prototype.propertiesShow = function (drawableId) {
        this.propertiesId = drawableId;
    };
    IkeDrawState.prototype.propertiesHide = function () {
        this.propertiesId = null;
    };
    IkeDrawState.prototype.setLinkFields = function (fieldIds) {
        if (fieldIds == null || fieldIds.length === 0) {
            this.linkFieldIds = [];
            if (this.tool !== IkeDrawTool.BASE) {
                this._tool = null;
            }
        }
        else {
            this.linkFieldIds = fieldIds;
            this._tool = IkeDrawTool.MARKER;
        }
    };
    IkeDrawState.prototype.setFromState = function (other) {
        if (other == null) {
            return;
        }
        if (this.brightness !== other.brightness) {
            this.setBrightness(other.brightness);
        }
        if (this.cropX !== other.cropX
            || this.cropY !== other.cropY
            || this.cropHeight !== other.cropHeight
            || this.cropWidth !== other.cropWidth) {
            this.setCrop(other.cropX, other.cropY, other.cropWidth, other.cropHeight);
        }
        this.setZoom(other.zoom, other.centerX, other.centerY);
    };
    IkeDrawState.prototype.setInfoVisible = function (visible) {
        this.isInfoVisible = visible;
        this.isBearingLabelVisible = visible;
        this.isDateLabelVisible = visible;
        this.isRollVisible = visible;
    };
    IkeDrawState.prototype.setInsightDetectionsVisible = function () {
        var visibleDetections = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            visibleDetections[_i] = arguments[_i];
        }
        if (!this.draw.drawState.hasInsightDetections()) {
            return;
        }
        this.visibleInsightDetections = visibleDetections;
        if (this.visibleInsightDetections.length !== 0) {
            this.setCrosshair(false);
        }
        else {
            if (!this.draw.drawState.hasUserMeasurements()) {
                this.setCrosshair(true);
            }
        }
    };
    IkeDrawState.prototype.cycleInsightDetectionsVisibility = function () {
        if (this.visibleInsightDetections.length === 0) {
            this.setInsightDetectionsVisible('marker');
        }
        else if (this.visibleInsightDetections.length === 1) {
            this.setInsightDetectionsVisible('marker', 'mask');
        }
        else {
            this.setInsightDetectionsVisible();
        }
    };
    IkeDrawState.prototype.arrangeAnnotations = function (renderOptions) {
        new ArrangeAnnotations_1.ArrangeAnnotations(this.draw, renderOptions).run();
    };
    IkeDrawState.BRIGHTNESS_DELTA = 1.2;
    IkeDrawState.BRIGHTNESS_MIN = 0.2;
    IkeDrawState.BRIGHTNESS_MAX = 5;
    IkeDrawState.BRIGHTNESS_APPROXIMATION_WINDOW = 0.1;
    IkeDrawState.BRIGHTNESS_DEFAULT = 1;
    IkeDrawState.IMAGE_VISIBILITY_HIDDEN_DEFAULT = false;
    IkeDrawState.ZOOM_DELTA = 1.1;
    IkeDrawState.ZOOM_MAX = image_zoom_binding_1.MAX_ZOOM_LEVEL;
    IkeDrawState.ZOOM_DEFAULT = image_zoom_binding_1.START_ZOOM_LEVEL;
    IkeDrawState.CABLEMEASURE_DEFAULT = 1; // default cablemesure value in inch
    IkeDrawState.CABLEMEASURE_STEP = 0.5;
    IkeDrawState.CABLEMEASURE_MIN = 0.5;
    IkeDrawState.CABLEMEASURE_MAX = 4;
    IkeDrawState.CROP_DEFAULT = 0; //default setting for x,y,width, and height.
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "zoom", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "cablemeasure", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "showCablemeasure", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "_brightness", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "_imageVisibilityHidden", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "_cropX", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "_cropY", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "_cropWidth", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "_cropHeight", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "scale", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "centerX", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "centerY", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "selectedRegion", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "selectedId", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "brightnessCorrectionId", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "imageVisibilityId", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "defaultBrightness", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "defaultImageVisibilityHidden", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "defaultCrop", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "cropParameterId", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "hoverId", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "moveId", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "linkFieldIds", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "propertiesId", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "isCrosshairVisible", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "isDateLabelVisible", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "isBearingLabelVisible", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "isRollVisible", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "isInfoVisible", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "visibleInsightDetections", void 0);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "areInsightDetectionsVisible", null);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "mouseDownX", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "mouseDownY", void 0);
    tslib_1.__decorate([
        mobx_1.observable
    ], IkeDrawState.prototype, "_tool", void 0);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "tool", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setTool", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "toggleTool", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "brightness", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "imageVisibilityHidden", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setBrightness", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setImageVisibilityHidden", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "toggleImageVisibility", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "brightnessIncrease", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "brightnessDecrease", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "canIncreaseBrightness", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "canDecreaseBrightness", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "canIncreaseCablemeasure", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "canDecreaseCablemeasure", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "brightnessReset", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "markerOffsetWidth", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "cropX", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "cropY", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "cropWidth", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "cropHeight", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setCrop", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "cropReset", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setCrosshair", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setDatelabel", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setBearingLabelVisible", null);
    tslib_1.__decorate([
        mobx_1.computed
    ], IkeDrawState.prototype, "center", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setHovered", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setCenter", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setSelected", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setBrightnessCorrectionId", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setImageVisibilityId", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setCropParameterId", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setZoom", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "zoomIncrease", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "zoomDecrease", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "zoomReset", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "toggleCablemeasure", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "cablemeasureIncrease", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "cablemeasureDecrease", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "propertiesShow", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "propertiesHide", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "setLinkFields", null);
    tslib_1.__decorate([
        mobx_1.action
    ], IkeDrawState.prototype, "arrangeAnnotations", null);
    return IkeDrawState;
}());
exports.IkeDrawState = IkeDrawState;
//# sourceMappingURL=draw.state.js.map